import React from 'react';
import useVacancySourcingService from '../useVacancySourcingService';
import { BadgeField, CommaListField, DateField, EnumField, TextField, useFieldHelper } from 'framework/ui/field';
import {
  DataGridInfiniteScrollPagingHandler,
  DataGridLayout,
  ListWithInfiniteScroll,
  RelationListLayout,
} from 'framework/list';
import CandidateLinkFieldWithName from '../../../shared/fields/CandidateLinkFieldWithName';
import { MAP_ENGLISH_LEVEL_TO_SHORT } from 'domain/shared/candidate';
import SourcingCandidatesFilters from '../shared/filter/SourcingCandidatesFilters';
import VacancyLinkField from 'domain/recruiting/vacancy/shared/input/VacancyLinkField';
import { Stages } from 'domain/shared/candidate';
import BlackListBadge from 'domain/recruiting/candidate/shared/BlackListBadge';
import PotentialCandidateBadge from 'domain/recruiting/candidate/shared/PotentialCandidateBadge';
import ReferralCandidateBadge from 'domain/recruiting/candidate/shared/ReferralCandidateBadge';
import StudentBadge from 'domain/recruiting/candidate/shared/StudentBadge';
import PriorityHigh from '@mui/icons-material/PriorityHigh';
import { Box } from '@mui/material';
import { Tooltip } from 'framework/ui/mui-builder';
import { UNCONFIRMED_QUALIFICATION_LEVEL_TOOLTIP } from 'domain/shared/candidate';

interface QualificationLevelFieldProps {
  label?: string;
  width?: string;
}

const QualificationLevelField = ({ label, width }: QualificationLevelFieldProps) => {
  const { getValue: getLevel } = useFieldHelper({
    source: 'level',
  });

  const { getValue: getIsQualificationLevelConfirmed } = useFieldHelper({
    source: 'isQualificationLevelConfirmed',
  });

  const level = getLevel();
  const isQualificationLevelConfirmed = String(getIsQualificationLevelConfirmed()) === 'true';

  return (
    <TextField
      label={label}
      source="level"
      width={width}
      value={
        level ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {level}

            {!isQualificationLevelConfirmed && (
              <Tooltip title={UNCONFIRMED_QUALIFICATION_LEVEL_TOOLTIP}>
                <PriorityHigh color="error" fontSize="small" />
              </Tooltip>
            )}
          </Box>
        ) : (
          ''
        )
      }
    />
  );
};

const SourcingCandidatesList = () => {
  const { search } = useVacancySourcingService();

  return (
    <ListWithInfiniteScroll getDataMethod={search}>
      <RelationListLayout filter={<SourcingCandidatesFilters />}>
        <DataGridInfiniteScrollPagingHandler>
          <DataGridLayout>
            <BadgeField
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
              badges={[
                <StudentBadge isVisible={({ isStudent }) => !!isStudent} />,
                <BlackListBadge isVisible={({ isInBlackList }) => !!isInBlackList} />,
                <PotentialCandidateBadge
                  isVisible={({ isMarkedAsPotentialCandidate }) => !!isMarkedAsPotentialCandidate}
                />,
                <ReferralCandidateBadge isVisible={({ isReferral }) => !!isReferral} />,
              ]}
              label="Name"
              source="id"
              width="18%"
            >
              <CandidateLinkFieldWithName nameSource="name.fullName" source="id" />
            </BadgeField>
            <TextField label="Specialization" source="specialization" width="14%" />

            <QualificationLevelField label="Level" width="6%" />

            <EnumField label="English" source="englishLevel" options={MAP_ENGLISH_LEVEL_TO_SHORT} width="6%" />
            <TextField label="Location" source="residenceCountry" width="10%" />
            <CommaListField label="Skills" source="skills" width="13%" />

            <VacancyLinkField source="lastAppliedVacancy" label="Last applied vacancy" width="13%" />

            <EnumField options={Stages} label="Last stage" source="lastStage" width="8%" />
            <DateField label="Last interaction" source="updatedAt" width="10%" showTime={false} isSortable />
          </DataGridLayout>
        </DataGridInfiniteScrollPagingHandler>
      </RelationListLayout>
    </ListWithInfiniteScroll>
  );
};

export default SourcingCandidatesList;
